import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetPassword } from "../services/store/authentication.actions";
import { LoadingSpinnerButton } from "../../_UI/Components/PathwayButtons";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import stringUtils from "../../_services/string-utils";
import { ErrorMessage } from "../../_UI/Components/Alerts/PathwayAlertMessages";
import styled from "styled-components";
import { authenticationReducerActions } from "../../_services/store";
import PathwayTextInputGroup from "../../_UI/Components/Inputs/PathwayTextInputGroup";

const PasswordRequirements = styled.div`
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
`;

const RequirementTitle = styled.h4`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.5rem 0;
`;

const RequirementsList = styled.ul`
    margin: 0;
    padding-left: 1.2rem;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: #6B7280;
`;

const RequirementItem = styled.li`
    margin-bottom: 0.25rem;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

export const SetPasswordInstructions = () => {
    return (
        <PasswordRequirements>
            <RequirementTitle>Passwort-Anforderungen:</RequirementTitle>
            <RequirementsList>
                <RequirementItem>mindestens zwölf Zeichen</RequirementItem>
                <RequirementItem>mindestens einem Großbuchstabe</RequirementItem>
                <RequirementItem>mindestens einer Zahl</RequirementItem>
                <RequirementItem>darf nur folgende Sonderzeichen enthalten: ?,!,_,*,.,@</RequirementItem>
            </RequirementsList>
        </PasswordRequirements>
    );
};

const ResetPasswordScreen = () => {
    const authentication = useSelector((state) => state.authentication);
    const dispatch = useDispatch();

    const [newPassword, setNewPassword] = useState("");
    const [newPasswordMatch, setNewPasswordMatch] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const { id } = useParams();

    const handleNewPasswordChange = useCallback((e) => {
        setNewPassword(e.target.value);
        dispatch(authenticationReducerActions.clearErrorMessage());
    }, [dispatch]);

    const handleNewPasswordMatchChange = useCallback((e) => {
        setNewPasswordMatch(e.target.value);
        dispatch(authenticationReducerActions.clearErrorMessage());
    }, [dispatch]); 

    const handleSubmit = useCallback(() => {
        setSubmitted(true);
        if (id && newPassword && newPasswordMatch) {
            dispatch(resetPassword(id, newPassword, newPasswordMatch)).then(() => {
                setSubmitted(false);
            });
        }
    }, [dispatch, setSubmitted, id, newPassword, newPasswordMatch]);

    if (stringUtils.isBlank(id)) {
        return (
            <SplitScreenContainer>
                <LeftSection>
                    <LeftLogoContainer>
                        <img
                            src="/logo.png"
                            alt="Pathway Logo"
                            style={{ maxWidth: "200px", marginBottom: "2rem" }}
                            className="rounded mx-auto d-block"
                        />
                    </LeftLogoContainer>
                    <HeaderText>
                        Das manuelle Buchen überspringen
                    </HeaderText>
                </LeftSection>
                <RightSection>
                    <ResetFormContainer>
                        <ErrorContainer>
                            <ErrorMessage marginless>Der Link zum zurücksetzen ist ungültig</ErrorMessage>
                        </ErrorContainer>
                    </ResetFormContainer>
                </RightSection>
            </SplitScreenContainer>
        );
    }

    return (
        <SplitScreenContainer>
            <LeftSection>
                <LeftLogoContainer>
                    <img
                        src="/logo_black.png"
                        alt="Pathway Logo"
                        style={{ maxWidth: "200px", marginBottom: "2rem" }}
                        className="rounded mx-auto d-block"
                    />
                </LeftLogoContainer>
                <HeaderText>
                   Das manuelle Buchen überspringen

                </HeaderText>
                <LegalFooter>
                    <LegalLink
                        href="https://www.pathway-solutions.de/impressum"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Impressum
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://app.pathway-solutions.de/Nutzungsvereinbarung_Platform.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Nutzungsvereinbarung
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://www.pathway-solutions.de/data-privacy-webapp"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Datenschutz
                    </LegalLink>
                </LegalFooter>
            </LeftSection>

            <RightSection>
                <ResetFormContainer>
                    <PageTitle>Passwort zurücksetzen</PageTitle>
                    <SubtitleText>
                        Erstelle ein neues sicheres Passwort für deinen pathway Account
                    </SubtitleText>

                    <ResetContent>
                        {authentication.errorMessage && (
                            <ErrorMessage marginless>{authentication.errorMessage}</ErrorMessage>
                        )}

                        <InputFieldContainer>
                            <FieldLabel>Neues Passwort</FieldLabel>
                            <PathwayTextInputGroup
                                type="password"
                                name="newPassword"
                                placeHolder="Dein neues Passwort"
                                value={newPassword}
                                handleChange={handleNewPasswordChange}
                                submitted={submitted}
                            />
                            {submitted && !newPassword && (
                                <ValidationError>Passwort ist ein Pflichtfeld</ValidationError>
                            )}
                        </InputFieldContainer>

                        <InputFieldContainer>
                            <FieldLabel>Passwort wiederholen</FieldLabel>
                            <PathwayTextInputGroup
                                type="password"
                                name="newPasswordMatch"
                                placeHolder="Passwort wiederholen"
                                value={newPasswordMatch}
                                handleChange={handleNewPasswordMatchChange}
                                submitted={submitted}
                            />
                            {submitted && !newPasswordMatch && (
                                <ValidationError>Passwort ist ein Pflichtfeld</ValidationError>
                            )}
                        </InputFieldContainer>

                        <PasswordRequirements>
                            <RequirementTitle>Passwort-Anforderungen:</RequirementTitle>
                            <RequirementsList>
                                <RequirementItem>mindestens zwölf Zeichen</RequirementItem>
                                <RequirementItem>mindestens einem Großbuchstabe</RequirementItem>
                                <RequirementItem>mindestens einer Zahl</RequirementItem>
                                <RequirementItem>darf nur folgende Sonderzeichen enthalten: ?,!,_,*,.,@</RequirementItem>
                            </RequirementsList>
                        </PasswordRequirements>

                        <ButtonContainer>
                            <LoadingSpinnerButton
                                onClick={handleSubmit}
                                loading={authentication && authentication.loading}
                            >
                                Passwort zurücksetzen
                            </LoadingSpinnerButton>
                        </ButtonContainer>

                        <BackToLoginContainer>
                            <BackToLoginLink to="/login">
                                ← Zurück zum Login
                            </BackToLoginLink>
                        </BackToLoginContainer>
                    </ResetContent>

                    <CompanyFooter>
                        <CompanyName>pathway solutions gmbh</CompanyName>
                    </CompanyFooter>
                </ResetFormContainer>
            </RightSection>
        </SplitScreenContainer>
    );
};

export default ResetPasswordScreen;

const SplitScreenContainer = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;
    
    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
        min-height: 100vh;
    }
`;

const LeftSection = styled.div`
    flex: 1;
    background: #EFFBFF;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1.5rem 1rem;
        min-height: 40vh;
        justify-content: flex-start;
        order: 2; /* Move to bottom on mobile */
    }
`;

const LeftLogoContainer = styled.div`
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
        margin-bottom: 1rem;
    }
`;

const HeaderText = styled.h2`
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    margin-bottom: 50px;
    line-height: 1.2;
    
    @media (max-width: 768px) {
        font-size: 22px;
        margin-bottom: 1.5rem;
        margin-top: 1rem;
    }
`;

const RightSection = styled.div`
    flex: 1;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1rem;
        align-items: flex-start;
        min-height: 60vh;
        order: 1; /* Move to top on mobile */
    }
`;

const ResetFormContainer = styled.div`
    width: 100%;
    max-width: 400px;
    
    @media (max-width: 768px) {
        max-width: 100%;
        padding: 0;
    }
`;

const PageTitle = styled.h1`
    font-family: 'Inter', sans-serif;
    font-size: 24px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
        font-size: 20px;
        margin-bottom: 0.75rem;
    }
`;

const SubtitleText = styled.p`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    text-align: center;
    color: #6B7280;
    margin-bottom: 3rem;
    line-height: 1.5;
    
    @media (max-width: 768px) {
        margin-bottom: 2rem;
        font-size: 13px;
    }
`;

const ResetContent = styled.div`
    display: grid;
    row-gap: 1.5rem;
`;

const InputFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    
    input {
        height: 45px !important;
    }
`;

const FieldLabel = styled.label`
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #1f2937;
    font-weight: 500;
    text-align: left;
`;

const ValidationError = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #dc2626;
    margin-top: 0.25rem;
`;

const ButtonContainer = styled.div`
    margin-top: 0;
    
    button {
        height: 45px;
        font-size: 16px;
    }
`;

const ErrorContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
`;

const BackToLoginContainer = styled.div`
    text-align: center;
    margin-top: 0.5rem;
    
    @media (max-width: 768px) {
        margin-top: 0.75rem;
    }
`;

const BackToLoginLink = styled(Link)`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6B7280;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
        color: #374151;
        text-decoration: underline;
    }
`;

const CompanyFooter = styled.div`
    margin-top: 1rem;
    text-align: center;
    
    @media (max-width: 768px) {
        margin-top: 1.5rem;
    }
`;

const CompanyName = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6B7280;
    text-align: center;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
        font-size: 12px;
        margin-bottom: 1rem;
    }
`;

const LegalFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 2rem;
    padding: 1rem;
    
    @media (max-width: 768px) {
        margin-top: auto;
        padding: 0.5rem;
        gap: 0.5rem;
        flex-wrap: wrap;
    }
`;

const LegalLink = styled.a`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #6B7280;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
        color: #374151;
        text-decoration: underline;
    }
    
    &:focus {
        outline: none;
        color: #374151;
    }
    
    @media (max-width: 768px) {
        font-size: 11px;
    }
`;

const LegalSeparator = styled.span`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #9CA3AF;
`;